import { defineStore } from "pinia";

import { loadUser } from "@/backend/http/user";
import { setLanguage } from "@/language";
import type { Board } from "@/model/board";
import { isPlanningBoard } from "@/model/board";
import type { Tenant } from "@/model/session";
import type { AuthUser, Role } from "@/model/user";
import { baseUser, technicalUser } from "@/model/user";
import { useGlobalI18n } from "@/translations/i18n";

const { t } = useGlobalI18n();

export function noAssignee(color: string): AuthUser {
  return noUser(color, /*$t*/ "general.noAssignee");
}

export function noReporter(color: string): AuthUser {
  return noUser(color, /*$t*/ "general.noReporter");
}

export function unassignedUser(color: string): AuthUser {
  return noUser(color, /*$t*/ "general.unassigned");
}

export function isNoUser(user: AuthUser): boolean {
  return user.type === "no";
}

function noUser(color: string, nameKey: string): AuthUser {
  return { ...baseUser, type: "no", id: "no", name: t(nameKey), color };
}

export const useUserStore = defineStore("user", {
  state: () => ({
    loginData: { email: "", tenants: [] as Tenant[] },
    technicalUser: technicalUser("", "", ""),
    currentUser: null as AuthUser | null,
  }),
  getters: {
    preferredLanguage(): string | undefined {
      return this.currentUser?.preferredLanguage;
    },
    isAllowed:
      (state) =>
      (permission: Permission, option: { orUserId?: string } = {}) =>
        state.technicalUser.id === option.orUserId ||
        permissions[state.technicalUser.role].includes(permission),
    isNonTeamZoomAllowed() {
      return (board: Board): boolean =>
        (!isPlanningBoard(board.type) && board.type !== "risk") || this.isAllowed("nonTeamZoom");
    },
    isTraining: (state) => state.technicalUser.company.startsWith("pips-"),
  },
  actions: {
    async loadCurrentUser() {
      this.currentUser = await loadUser(
        { id: this.technicalUser.id, caller: "loadCurrentUser" },
        { useCache: false },
      );
      if (this.preferredLanguage) {
        await setLanguage(this.preferredLanguage);
      }
      return this.currentUser;
    },
    resetUser() {
      this.technicalUser = technicalUser("", "", "");
    },
  },
});

type Permission =
  | "edit"
  | "nonTeamZoom"
  | "deleteBoard"
  | "cockpit"
  | "useWithoutLicense"
  | "editBoard"
  | "duplicateFlex"
  | "manageConfidenceVote"
  | "contactSupport"
  | "configureAlm";

const permissions: Record<Role, Permission[]> = {
  observer: [],
  user: ["edit", "editBoard"],
  planning_interval_admin: [
    "edit",
    "nonTeamZoom",
    "deleteBoard",
    "cockpit",
    "editBoard",
    "duplicateFlex",
    "manageConfidenceVote",
  ],
  admin: [
    "edit",
    "nonTeamZoom",
    "deleteBoard",
    "cockpit",
    "useWithoutLicense",
    "editBoard",
    "duplicateFlex",
    "manageConfidenceVote",
    "contactSupport",
    "configureAlm",
  ],
};
